import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { FaRegCircleCheck } from "react-icons/fa6";
import { capitalizeNames } from "app/utils/utils";

interface InsuredSignature {
  insured: { profile: { firstName: string; lastName: string; profilePicture?: string | null } };
  insuredAge: number;
  confirmed: boolean | null;
  confirmedDate: string | null;
  status: boolean | string | null;
}

interface AdvisorSignature {
  profile: { firstName: string; lastName: string };
  confirmed: boolean;
  confirmedDate: string | null;
  status: boolean;
}

interface IRCSignaturesPageProps {
  signatures: {
    advisor: AdvisorSignature;
    insureds: InsuredSignature[];
    primaryContact?: any;
  };
  pageNumber: string | number;
  showAdvisor?: boolean;
  showDisclaimer?: boolean;
  insuredSlice?: InsuredSignature[];
}

const dateFormat = (d: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
    : null;

const isApproved = (confirmed: boolean | null, status: boolean | string | null) =>
  confirmed === true || status === true;

const isDeclined = (confirmed: boolean | null, status: boolean | string | null) =>
  confirmed === false || status === false;

const isSkipped = (status: boolean | string | null) => status === "skipped";

const IRCSignaturesPage = ({
  signatures,
  pageNumber,
  showAdvisor = true,
  showDisclaimer = true,
  insuredSlice,
}: IRCSignaturesPageProps) => {
  const { advisor, insureds } = signatures;
  const visibleInsureds = insuredSlice ?? insureds;

  const advisorName = advisor?.profile
    ? capitalizeNames(`${advisor.profile.firstName} ${advisor.profile.lastName}`)
    : "—";
  const advisorApproved = isApproved(advisor?.confirmed ?? null, advisor?.status ?? null);

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[4em] w-full max-w-[56em] mx-auto p-[1.75em]">
        <h1 className="text-center text-2xl font-bold mb-5 conthrax-font interactive-proposal-text">
          Signatures
        </h1>

        {showDisclaimer && (
          <p className="mb-3 leading-relaxed" style={{ fontSize: "13px", color: "#000000" }}>
            I acknowledge that I have reviewed the full Interactive Review Center report, including
            all disclosures. I understand that all projected values and interest rates are
            hypothetical, not guaranteed, and subject to change.
          </p>
        )}

        {/* Advisor row */}
        {showAdvisor && (
          <>
            <p
              className="font-bold mb-2 conthrax-font"
              style={{
                fontSize: "13px",
                color: "#091927",
                borderBottom: "1px solid #CCCCCC",
                paddingBottom: "4px",
              }}
            >
              Advisor
            </p>
            <div className="flex gap-4 mb-8">
              <div className="flex flex-col w-full">
                <div
                  className="text-sm text-center font-bold align-content-end"
                  style={{ minHeight: "40px", color: "black" }}
                >
                  {advisorName}
                </div>
                <div className="border-gray-400 h-1 mb-2 border-bottom-2" />
                <label className="text-sm text-center" style={{ color: "#616161" }}>
                  Advisor Name
                </label>
              </div>
              <div className="flex flex-col w-full">
                <div
                  className="text-sm text-center font-bold align-content-end flex items-end justify-center"
                  style={{ minHeight: "40px", color: "black" }}
                >
                  {advisorApproved && <FaRegCircleCheck className="mr-2 mb-1" />}
                  {advisorApproved ? "Confirmed" : "—"}
                </div>
                <div className="border-gray-400 h-1 mb-2 border-bottom-2" />
                <label className="text-sm text-center" style={{ color: "#616161" }}>
                  Signature
                </label>
              </div>
              <div className="flex flex-col w-full">
                <div
                  className="text-sm text-center font-bold align-content-end"
                  style={{ minHeight: "40px", color: "black" }}
                >
                  {dateFormat(advisor?.confirmedDate) || "—"}
                </div>
                <div className="border-gray-400 h-1 mb-2 border-bottom-2" />
                <label className="text-sm text-center" style={{ color: "#616161" }}>
                  Date
                </label>
              </div>
            </div>
          </>
        )}

        {/* Insureds */}
        <p
          className="font-bold mb-2 conthrax-font"
          style={{
            fontSize: "13px",
            color: "#091927",
            borderBottom: "1px solid #CCCCCC",
            paddingBottom: "4px",
          }}
        >
          Insureds
        </p>
        {visibleInsureds?.map((ins, i) => {
          const name = capitalizeNames(
            `${ins.insured?.profile?.firstName ?? ""} ${ins.insured?.profile?.lastName ?? ""}`,
          );
          const approved = isApproved(ins.confirmed, ins.status);
          const declined = isDeclined(ins.confirmed, ins.status);
          const skipped = isSkipped(ins.status);
          const statusLabel = approved
            ? "Confirmed"
            : declined
            ? "Declined"
            : skipped
            ? "Skipped"
            : "Pending";

          return (
            <div key={i} className="flex gap-4 mb-6">
              <div className="flex flex-col w-full">
                <div
                  className="text-sm text-center font-bold align-content-end"
                  style={{ minHeight: "40px", color: "black" }}
                >
                  {name}
                  <div style={{ fontSize: "11px", color: "#9CA3AF", fontWeight: 400 }}>
                    Age {ins.insuredAge}
                  </div>
                </div>
                <div className="border-gray-400 h-1 mb-2 border-bottom-2" />
                <label className="text-sm text-center" style={{ color: "#616161" }}>
                  Insured Name
                </label>
              </div>
              <div className="flex flex-col w-full">
                <div
                  className="text-sm text-center font-bold align-content-end flex items-end justify-center"
                  style={{ minHeight: "40px", color: "black" }}
                >
                  {approved && <FaRegCircleCheck className="mr-2 mb-1" />}
                  {statusLabel}
                </div>
                <div className="border-gray-400 h-1 mb-2 border-bottom-2" />
                <label className="text-sm text-center" style={{ color: "#616161" }}>
                  Signature
                </label>
              </div>
              <div className="flex flex-col w-full">
                <div
                  className="text-sm text-center font-bold align-content-end"
                  style={{ minHeight: "40px", color: "black" }}
                >
                  {dateFormat(ins.confirmedDate) || "—"}
                </div>
                <div className="border-gray-400 h-1 mb-2 border-bottom-2" />
                <label className="text-sm text-center" style={{ color: "#616161" }}>
                  Date
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCSignaturesPage;
